import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
  styled,
} from "@mui/material";
import moment from "moment";
import { toast } from "react-toastify";
import {
  getContractorProposal,
  getCustomerPaymentLines,
  getCustomerPaymentSummary,
  getHomeownerPaymentLines,
} from "../../../services/contractorService";
import {
  renderCurrencyIcon,
  renderCurrencyIconColored,
} from "../../../utils/CurrencyUtils";
import { formattedAmountWithoutAed } from "../../../utils/CommonFunction";

// Three buckets shown in the analytics row + progress bar. Palette and
// sizing match Settlements.js so the two tabs read identically.
const PAID_COLOR = "#4BB34B";       // green
const OVERDUE_COLOR = "#FF971C";    // amber (semantic: past due)
const NOT_OVERDUE_COLOR = "#C3C6D6"; // gray (semantic: not yet due)
const PAID_GREEN = "#16A34A";       // for the row-level Paid status pill

const StyledTableCell = styled(TableCell)(() => ({
  borderBottom: "none",
  fontFamily: "Inter Tight",
}));

const formatStatusLabel = (s) =>
  String(s || "")
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

// Single bucket on the analytics row — sizing/typography mirrors the
// Bucket in Settlements.js (14px label, 18px value, 12px dot).
const Bucket = ({ label, amount, dotColor }) => (
  <Box sx={{ flex: 1, minWidth: { xs: 0, md: 140 } }}>
    <Typography
      sx={{
        fontSize: 14,
        color: "#949DAA",
        fontFamily: "Inter Tight",
        fontWeight: 500,
        mb: 1,
      }}
    >
      {label}
    </Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: "50%",
          backgroundColor: dotColor,
          flexShrink: 0,
        }}
      />
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "2px",
          fontSize: 18,
          fontWeight: 500,
          color: "#0D0D0E",
          fontFamily: "Inter Tight",
        }}
      >
        {renderCurrencyIcon("black-small")}
        {formattedAmountWithoutAed(Number(amount) || 0)}
      </Box>
    </Box>
  </Box>
);

// Multi-segment progress bar — 10px height + 5px radius to match
// Settlements.js. Empty segments collapse, leaving a clean visual.
const SegmentedProgressBar = ({ segments }) => {
  const total = segments.reduce((a, s) => a + (s.value || 0), 0);
  if (total <= 0) {
    return (
      <Box
        sx={{
          height: 10,
          borderRadius: "5px",
          backgroundColor: "#E5E7EB",
          mt: 1.5,
        }}
      />
    );
  }
  return (
    <Box
      sx={{
        display: "flex",
        height: 10,
        borderRadius: "5px",
        overflow: "hidden",
        backgroundColor: "#E5E7EB",
        mt: 1.5,
      }}
    >
      {segments.map((s, i) => {
        const pct = ((s.value || 0) / total) * 100;
        if (pct <= 0) return null;
        return (
          <Box
            key={i}
            sx={{ width: `${pct}%`, backgroundColor: s.color }}
          />
        );
      })}
    </Box>
  );
};

const StatusPill = ({ status }) => {
  const upper = String(status || "").toUpperCase();
  const isPaid = upper === "PAID";
  // CREATED is the backend's "scheduled but nothing happened yet" state
  // and reads as noisy when echoed verbatim — render a dash instead.
  const isPlaceholder = !upper || upper === "CREATED";
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: isPaid ? PAID_GREEN : "#D1D5DB",
        }}
      />
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 500,
          color: isPaid ? "#0D0D0E" : "#6B7280",
          fontFamily: "Inter Tight",
        }}
      >
        {isPlaceholder ? "-" : formatStatusLabel(status)}
      </Typography>
    </Box>
  );
};

const formatDate = (val) =>
  val ? moment(val).format("MMM DD, YYYY") : "-";

const formatStageLabel = (s) => {
  if (!s) return "";
  const lower = String(s).toLowerCase().replace(/_/g, " ");
  return lower.replace(/\b\w/g, (c) => c.toUpperCase());
};

const getApiStatusCode = (res) => {
  const code = res?.statusCode ?? res?.status_code ?? res?.code;
  if (code == null) return null;
  const parsed = Number(code);
  return Number.isFinite(parsed) ? parsed : null;
};

// Empty state shown on Customer Payments + Settlements when stage is
// REQUEST — billing milestones haven't been set up yet, so neither
// payouts nor payment lines exist on the backend.
const SetupBillingMilestonesEmptyState = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      py: 8,
    }}
  >
    <Typography
      sx={{
        fontSize: 14,
        color: "#6B7280",
        fontFamily: "Inter Tight",
        fontWeight: 500,
      }}
    >
      Setup Billing Milestones first
    </Typography>
  </Box>
);

// Initial Deposit (DOWNPAYMENT) is always pinned to the top — the
// backend orders by createdAt which doesn't always match the desired
// presentation order.
const sortDownpaymentFirst = (schedule) => {
  if (!Array.isArray(schedule) || schedule.length <= 1) return schedule || [];
  const idx = schedule.findIndex((s) => {
    const t = String(s?.type || "").toUpperCase();
    if (t === "DOWNPAYMENT") return true;
    return String(s?.displayName || "").trim().toLowerCase() === "initial deposit";
  });
  if (idx <= 0) return schedule;
  return [
    schedule[idx],
    ...schedule.slice(0, idx),
    ...schedule.slice(idx + 1),
  ];
};

// Plain text tabs with a blue underline on the active one — same look
// as the duration-selector tabs ("3 months / 6 months / 12 months /
// Escrow") used elsewhere in the contractor portal.
const InnerTab = styled(Tab)(() => ({
  color: "#0D0D0E",
  textTransform: "none",
  fontFamily: "Inter Tight",
  fontSize: "14px",
  fontWeight: 500,
  letterSpacing: 0,
  minHeight: "40px",
  padding: "8px 0",
  marginRight: "32px",
  minWidth: 0,
  "&.Mui-selected": {
    color: "#4D6BF6",
    fontWeight: 600,
  },
  "&:hover": {
    color: "#4D6BF6",
  },
}));

const PaymentScheduleTable = ({ schedule, milestoneNameById }) => {
  const rows = useMemo(() => sortDownpaymentFirst(schedule || []), [schedule]);
  const showMilestoneColumn =
    milestoneNameById instanceof Map && milestoneNameById.size > 0;

  if (!rows.length) {
    return (
      <Typography
        sx={{
          fontSize: 14,
          color: "#9CA3AF",
          fontFamily: "Inter Tight",
          textAlign: "center",
          py: 4,
        }}
      >
        No payments scheduled yet.
      </Typography>
    );
  }

  return (
    <>
      {/* Desktop / tablet — original table. */}
      <TableContainer
        sx={{
          boxShadow: "none",
          backgroundColor: "transparent",
          display: { xs: "none", md: "block" },
        }}
      >
        <Table
          sx={{
            "& thead th": {
              color: "#9CA3AF",
              fontWeight: 500,
              fontSize: 13,
              fontFamily: "Inter Tight",
              borderBottom: "none",
              paddingTop: "12px",
              paddingBottom: "12px",
            },
            "& tbody tr:hover": { backgroundColor: "#F9FAFB" },
          }}
        >
          <TableHead>
            <TableRow>
              <StyledTableCell>Payment</StyledTableCell>
              {showMilestoneColumn && (
                <StyledTableCell>Billing Milestone</StyledTableCell>
              )}
              <StyledTableCell>Due Date</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell align="right">
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    justifyContent: "flex-end",
                  }}
                >
                  Amount ({renderCurrencyIconColored("black-xs-small")})
                </Box>
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, idx) => (
              <TableRow key={`${r.displayName || "row"}-${idx}`}>
                <StyledTableCell sx={{ py: 2 }}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#0D0D0E",
                      fontFamily: "Inter Tight",
                    }}
                  >
                    {r.displayName || "-"}
                  </Typography>
                </StyledTableCell>
                {showMilestoneColumn && (
                  <StyledTableCell sx={{ fontSize: 13, color: "#0D0D0E" }}>
                    {r.billingMilestoneId != null
                      ? milestoneNameById.get(String(r.billingMilestoneId)) ||
                        "—"
                      : "—"}
                  </StyledTableCell>
                )}
                <StyledTableCell sx={{ fontSize: 13, color: "#6B7280" }}>
                  {formatDate(r.dueDate)}
                </StyledTableCell>
                <StyledTableCell>
                  <StatusPill status={r.status} />
                </StyledTableCell>
                <StyledTableCell align="right">
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      justifyContent: "flex-end",
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#0D0D0E",
                      fontFamily: "Inter Tight",
                    }}
                  >
                    {renderCurrencyIconColored("black-xs-small")}
                    {formattedAmountWithoutAed(Number(r.amount) || 0)}
                  </Box>
                </StyledTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Mobile — stacked cards. */}
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          flexDirection: "column",
        }}
      >
        {rows.map((r, idx) => (
          <Box
            key={`m-${r.displayName || "row"}-${idx}`}
            sx={{
              borderBottom: "1px solid #E5E7EB",
              background: "#fff",
              py: 1.5,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#0D0D0E",
                  fontFamily: "Inter Tight",
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {r.displayName || "-"}
              </Typography>
              <StatusPill status={r.status} />
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "#9CA3AF",
                    fontFamily: "Inter Tight",
                    mb: 0.25,
                  }}
                >
                  Due Date
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: "#0D0D0E",
                    fontFamily: "Inter Tight",
                  }}
                >
                  {formatDate(r.dueDate)}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "#9CA3AF",
                    fontFamily: "Inter Tight",
                    mb: 0.25,
                  }}
                >
                  Amount
                </Typography>
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#0D0D0E",
                    fontFamily: "Inter Tight",
                  }}
                >
                  {renderCurrencyIconColored("black-xs-small")}
                  {formattedAmountWithoutAed(Number(r.amount) || 0)}
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </>
  );
};

// One inner tab per installment plan — labelled by projectPaymentType
// (Escrow / etc). The table body comes from each plan's paymentSchedule.
const InstallmentPlanTabs = ({ plans, milestoneNameById }) => {
  const [active, setActive] = useState(0);
  if (!Array.isArray(plans) || plans.length === 0) {
    return (
      <Typography
        sx={{
          fontSize: 14,
          color: "#9CA3AF",
          fontFamily: "Inter Tight",
          textAlign: "center",
          py: 4,
        }}
      >
        No customer payment plan yet.
      </Typography>
    );
  }
  const safeIndex = Math.min(active, plans.length - 1);
  const current = plans[safeIndex];
  return (
    <Box sx={{ p: 1 , width: "75%",}}>
      <Tabs
        value={safeIndex}
        onChange={(_, v) => setActive(v)}
        variant="scrollable"
        scrollButtons={false}
        TabIndicatorProps={{
          sx: { backgroundColor: "#4D6BF6", height: "2px" },
        }}
        sx={{
          mb: 2,
          minHeight: "40px",
          borderBottom: "1px solid #E5E7EB",
          "& .MuiTabs-scroller": { scrollbarWidth: "none" },
          "& .MuiTabs-scroller::-webkit-scrollbar": { display: "none" },
        }}
      >
        {plans.map((p, i) => (
          <InnerTab
            key={p.id ?? i}
            label={
              formatStageLabel(p.projectPaymentType) ||
              p.displayName ||
              `Plan ${i + 1}`
            }
          />
        ))}
      </Tabs>
      <PaymentScheduleTable
        schedule={current?.paymentSchedule || []}
        milestoneNameById={milestoneNameById}
      />
    </Box>
  );
};

// "X days passed" — only for lines where the due date has elapsed and
// the line isn't yet PAID. Otherwise we render "-".
const computeDpd = (row) => {
  if (!row?.dueDate) return null;
  if (String(row.status || "").toUpperCase() === "PAID") return null;
  const due = moment(row.dueDate).startOf("day");
  const today = moment().startOf("day");
  const days = today.diff(due, "days");
  if (days <= 0) return null;
  return days;
};

export default function CustomerPayments({ projectId, proposalId, stage }) {
  const upperStage = String(stage || "").toUpperCase();
  const isProposalStage = upperStage === "PROPOSAL";
  const isRequestStage = upperStage === "REQUEST";

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  // Proposal-stage installment plans from
  // /api/v2/paymentlines/homeowner/proposal/:proposalId. Each plan has a
  // projectPaymentType (used as the inner tab label) and a
  // paymentSchedule[] used as the table body inside the tab.
  const [installmentPlans, setInstallmentPlans] = useState([]);
  // Proposal-stage billing milestones (from the contractor proposal
  // endpoint). Used to map each payment line's billingMilestoneId back
  // to a human-readable milestone group name in the table.
  const [proposalMilestones, setProposalMilestones] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Wait until stage is known to avoid hitting proposal APIs first and
      // then project APIs once proposal data arrives.
      if (!upperStage) return;

      // REQUEST stage skips the network — the empty-state ("Setup Billing
      // Milestones first") doesn't depend on any payload.
      if (isRequestStage) {
        setRows([]);
        setSummary(null);
        setInstallmentPlans([]);
        return;
      }

      if (isProposalStage) {
        if (!proposalId) return;
        setLoading(true);
        try {
          // Fetch the homeowner payment lines + the contractor proposal
          // in parallel — the proposal payload contains
          // billing_milestones, which we need to resolve each line's
          // billingMilestoneId into a readable milestone name.
          const [linesRes, proposalRes] = await Promise.all([
            getHomeownerPaymentLines(proposalId),
            getContractorProposal(proposalId).catch(() => null),
          ]);
          const statusCode = getApiStatusCode(linesRes);
          if (statusCode != null && statusCode !== 2000) {
            toast.error(linesRes?.message || "Failed to load customer payments");
            setInstallmentPlans([]);
            return;
          }
          if (cancelled) return;
          const payload = linesRes?.data ?? linesRes ?? null;
          const plans = Array.isArray(payload?.installmentPlans)
            ? payload.installmentPlans
            : [];
          setInstallmentPlans(plans);
          const proposalData = proposalRes?.data ?? proposalRes ?? null;
          const milestones = Array.isArray(proposalData?.billing_milestones)
            ? proposalData.billing_milestones
            : [];
          setProposalMilestones(milestones);
        } catch (e) {
          console.error("Failed to load homeowner payment lines", e);
        } finally {
          if (!cancelled) setLoading(false);
        }
        return;
      }

      if (!projectId) return;
      setLoading(true);
      try {
        const [linesRes, summaryRes] = await Promise.all([
          getCustomerPaymentLines({ projectId, size: 50 }).catch(() => null),
          getCustomerPaymentSummary(projectId).catch(() => null),
        ]);
        const linesStatusCode = getApiStatusCode(linesRes);
        if (linesStatusCode != null && linesStatusCode !== 2000) {
          toast.error(linesRes?.message || "Failed to load customer payments");
          setRows([]);
          setSummary(null);
          return;
        }
        const summaryStatusCode = getApiStatusCode(summaryRes);
        if (summaryStatusCode != null && summaryStatusCode !== 2000) {
          toast.error(summaryRes?.message || "Failed to load payment summary");
          setRows([]);
          setSummary(null);
          return;
        }
        if (cancelled) return;
        const list = Array.isArray(linesRes)
          ? linesRes
          : Array.isArray(linesRes?.data)
          ? linesRes.data
          : [];
        setRows(list);
        // Backend returns the summary as a single-element array keyed by
        // projectId; unwrap it so `summary.totalUnPaidAmount` etc. resolve.
        const summaryPayload = summaryRes?.data ?? summaryRes ?? null;
        const summaryObj = Array.isArray(summaryPayload)
          ? summaryPayload.find((s) => s?.projectId === projectId) ||
            summaryPayload[0] ||
            null
          : summaryPayload;
        setSummary(summaryObj);
      } catch (e) {
        console.error("Failed to load customer payments", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, proposalId, isProposalStage, isRequestStage, upperStage]);

  // Backend exposes totalPaidAmount + totalUnPaidAmount + totalDueAmount.
  // The screenshot's three buckets map as:
  //   Total Paid   ↔ totalPaidAmount   (blue)
  //   Overdue      ↔ totalDueAmount    (amber)
  //   Not Overdue  ↔ totalUnPaidAmount (gray) — i.e. unpaid but not yet due

  const totals = useMemo(() => {
    const paid = Number(summary?.totalPaidAmount) || 0;
    const overdue = Number(summary?.totalDueAmount) || 0;
    const notOverdue = Number(summary?.totalUnPaidAmount) || 0;
    return { paid, overdue, notOverdue };
  }, [summary]);

  // Lookup: billingMilestoneId → groupName, used by PaymentScheduleTable
  // to render the billing-milestone column for proposal-stage plans.
  const milestoneNameById = useMemo(() => {
    const map = new Map();
    (proposalMilestones || []).forEach((m) => {
      if (m && m.id != null) {
        map.set(
          String(m.id),
          m.groupName || m.description || `Milestone ${m.id}`,
        );
      }
    });
    return map;
  }, [proposalMilestones]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 6,
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (isRequestStage) {
    return <SetupBillingMilestonesEmptyState />;
  }

  if (isProposalStage) {
    return (
      <InstallmentPlanTabs
        plans={installmentPlans}
        milestoneNameById={milestoneNameById}
      />
    );
  }

  return (
    <Box sx={{ p: 1, width: { xs: "100%", md: "75%" } }}>
      {/* Analytics row + segmented progress bar. On mobile the three
          buckets reflow into a 2-column grid so the row doesn't overflow
          horizontally. */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: { xs: "grid", md: "flex" },
            gridTemplateColumns: { xs: "1fr 1fr", md: "none" },
            gap: { xs: 1.5, md: 4 },
            flexWrap: "wrap",
            alignItems: "flex-start",
          }}
        >
          <Bucket
            label="Paid"
            amount={totals.paid}
            dotColor={PAID_COLOR}
          />
          <Bucket
            label="Due"
            amount={totals.overdue}
            dotColor={OVERDUE_COLOR}
          />
          <Bucket
            label="Not Due"
            amount={totals.notOverdue}
            dotColor={NOT_OVERDUE_COLOR}
          />
        </Box>
        <SegmentedProgressBar
          segments={[
            { value: totals.paid, color: PAID_COLOR },
            { value: totals.overdue, color: OVERDUE_COLOR },
            { value: totals.notOverdue, color: NOT_OVERDUE_COLOR },
          ]}
        />
      </Box>

      {rows.length === 0 ? (
        <Typography
          sx={{
            fontSize: 14,
            color: "#9CA3AF",
            fontFamily: "Inter Tight",
            textAlign: "center",
            py: 4,
          }}
        >
          No customer payments yet.
        </Typography>
      ) : (
        <TableContainer
          sx={{
            boxShadow: "none",
            backgroundColor: "transparent",
            display: { xs: "none", md: "block" },
          }}
        >
          <Table
            sx={{
              "& thead th": {
                color: "#9CA3AF",
                fontWeight: 500,
                fontSize: 13,
                fontFamily: "Inter Tight",
                borderBottom: "none",
                paddingTop: "12px",
                paddingBottom: "12px",
              },
              "& tbody tr:hover": { backgroundColor: "#F9FAFB" },
            }}
          >
            <TableHead>
              <TableRow>
                <StyledTableCell>Payment</StyledTableCell>
                <StyledTableCell>Due Date</StyledTableCell>
                <StyledTableCell>Payment Method</StyledTableCell>
                <StyledTableCell align="right">
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      justifyContent: "flex-end",
                    }}
                  >
                    Amount Paid ({renderCurrencyIconColored("black-xs-small")})
                  </Box>
                </StyledTableCell>
                <StyledTableCell>Payment Date</StyledTableCell>
                <StyledTableCell>Processed by</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const dpd = computeDpd(r);
                const isPaid =
                  String(r.status || "").toUpperCase() === "PAID";
                return (
                  <TableRow key={r.id}>
                    <StyledTableCell sx={{ py: 2 }}>
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: "#0D0D0E",
                          fontFamily: "Inter Tight",
                        }}
                      >
                        {r.displayName || "-"}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: "#9CA3AF",
                          fontFamily: "Inter Tight",
                          mt: 0.25,
                        }}
                      >
                        ID: {r.id}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{ fontSize: 13, color: "#6B7280" }}
                    >
                      {formatDate(r.dueDate)}
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{ fontSize: 13, color: "#6B7280" }}
                    >
                      {r.paymentMethod || "-"}
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <Box
                        component="span"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          justifyContent: "flex-end",
                          fontSize: 14,
                          fontWeight: 600,
                          color: "#0D0D0E",
                          fontFamily: "Inter Tight",
                        }}
                      >
                        {renderCurrencyIconColored("black-xs-small")}
                        {Number(r.amount) || 0}
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{ fontSize: 13, color: "#6B7280" }}
                    >
                      {isPaid && r.paymentEventTime
                        ? formatDate(r.paymentEventTime)
                        : "-"}
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{ fontSize: 13, color: "#6B7280" }}
                    >
                      {r.processedBy || r.processed_by || "-"}
                    </StyledTableCell>
                    <StyledTableCell>
                      <StatusPill status={r.status} />
                    </StyledTableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Mobile — cards for the customer payment listing. */}
      {rows.length > 0 && (
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            flexDirection: "column",
          }}
        >
          {rows.map((r) => {
            const isPaid =
              String(r.status || "").toUpperCase() === "PAID";
            return (
              <Box
                key={`m-${r.id}`}
                sx={{
                  borderBottom: "1px solid #E5E7EB",
                  background: "#fff",
                  py: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: "#0D0D0E",
                        fontFamily: "Inter Tight",
                      }}
                    >
                      {r.displayName || "-"}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "#9CA3AF",
                        fontFamily: "Inter Tight",
                        mt: 0.25,
                      }}
                    >
                      ID: {r.id}
                    </Typography>
                  </Box>
                  <StatusPill status={r.status} />
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1.25,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "#9CA3AF",
                        fontFamily: "Inter Tight",
                        mb: 0.25,
                      }}
                    >
                      Due Date
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 13,
                        color: "#0D0D0E",
                        fontFamily: "Inter Tight",
                      }}
                    >
                      {formatDate(r.dueDate)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "#9CA3AF",
                        fontFamily: "Inter Tight",
                        mb: 0.25,
                      }}
                    >
                      Amount Paid
                    </Typography>
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        fontSize: 14,
                        fontWeight: 600,
                        color: "#0D0D0E",
                        fontFamily: "Inter Tight",
                      }}
                    >
                      {renderCurrencyIconColored("black-xs-small")}
                      {Number(r.amount) || 0}
                    </Box>
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "#9CA3AF",
                        fontFamily: "Inter Tight",
                        mb: 0.25,
                      }}
                    >
                      Payment Method
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 13,
                        color: "#0D0D0E",
                        fontFamily: "Inter Tight",
                      }}
                    >
                      {r.paymentMethod || "-"}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "#9CA3AF",
                        fontFamily: "Inter Tight",
                        mb: 0.25,
                      }}
                    >
                      Payment Date
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 13,
                        color: "#0D0D0E",
                        fontFamily: "Inter Tight",
                      }}
                    >
                      {isPaid && r.paymentEventTime
                        ? formatDate(r.paymentEventTime)
                        : "-"}
                    </Typography>
                  </Box>
                </Box>

                <Box>
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: "#9CA3AF",
                      fontFamily: "Inter Tight",
                      mb: 0.25,
                    }}
                  >
                    Processed by
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: "#0D0D0E",
                      fontFamily: "Inter Tight",
                    }}
                  >
                    {r.processedBy || r.processed_by || "-"}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
